<script lang="ts">import { onDestroy } from "svelte";
import { AppMode, cfgAppMode, cfgEventThemeCdnPath, cfgToThemeCdnPath, FeatureSet, isEventAssetEnabled, RELEASE, ReleaseStage, Stage, stateFeatureSet, stateUi, wantAtLeastReleaseStage } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isFullScreenGame, isMobile, loggedIn, ModalDialog, selectedItemMobileMenu, stateOpenModal, toSiteUrl } from "@infinitro/global";
let lobbyHref = "/";
let logoPressTimer = null;
let isLogoLongPressed = false;
// Reactive statements for event logo
$: useEventLogo = isEventAssetEnabled("customLogo");
$: useAlternative = $stateUi.header.headerStyle === "alternative";
$: useAlternative2 = $stateUi.header.headerStyle === "alternative2";
$: useAlternative4 = $stateUi.header.headerStyle === "alternative4";
$: useAlternative2PublicLogo = useAlternative2 && !$loggedIn;
$: useUnboundedLogoWidth = $stateUi.header?.useUnboundedLogoWidth === true;
$: useDesktopLogoOnMobileByBrand = $isMobile && $stateUi.games.showLogoWhenPlayingDesktop;
$: useDesktopLogoOnMobileGame = $isMobile && $isFullScreenGame && $loggedIn && $stateUi.games.showNavigationOnMobile;
$: useDesktopLogoOnMobile = useDesktopLogoOnMobileByBrand || useDesktopLogoOnMobileGame;
$: desktopLogoPath = useEventLogo ? cfgEventThemeCdnPath("customlogo.png") : cfgToThemeCdnPath("/misc/logo.svg");
$: mobileLogoPath = useDesktopLogoOnMobileGame && $loggedIn ? desktopLogoPath : useEventLogo ? cfgEventThemeCdnPath("customlogomobile.png") : useAlternative2 ? cfgToThemeCdnPath("/misc/logo.svg") : cfgToThemeCdnPath("/misc/logo-mobile.svg");
const startPressLogo = (event) => {
	logoPressTimer = setTimeout(() => {
		isLogoLongPressed = true;
		console.log("logo was long-pressed");
		stateOpenModal.set(ModalDialog.DevSettings);
	}, RELEASE === Stage.Prod ? 7e3 : 3e3);
};
stateOpenModal.subscribe((value) => {
	isLogoLongPressed = value === ModalDialog.DevSettings;
});
const logoClick = (event) => {
	selectedItemMobileMenu.set(null);
	if (isLogoLongPressed) {
		event?.preventDefault();
		return false;
	}
};
const endPressLogo = (event) => {
	if (logoPressTimer) {
		clearTimeout(logoPressTimer);
		logoPressTimer = null;
	}
	if (isLogoLongPressed) {
		event?.preventDefault();
		return false;
	}
	isLogoLongPressed = false;
};
const TAP_MOVE_TOLERANCE_PX = 10;
// Android WebView locks the link for its native long-press menu on touchdown (cancelling the
// dev-settings timer via touchcancel) and ignores contextmenu preventDefault. So the logo owns its
// touches: the default is cancelled on a non-passive touchstart and a short tap is replayed as a click.
const logoTouch = (node) => {
	let startX = 0;
	let startY = 0;
	let moved = false;
	const onTouchStart = (event) => {
		event.preventDefault();
		startX = event.touches?.[0]?.clientX ?? 0;
		startY = event.touches?.[0]?.clientY ?? 0;
		moved = false;
		startPressLogo(event);
	};
	const onTouchMove = (event) => {
		const touch = event.touches?.[0];
		if (!moved && touch && Math.hypot(touch.clientX - startX, touch.clientY - startY) > TAP_MOVE_TOLERANCE_PX) {
			moved = true;
			endPressLogo(event);
		}
	};
	const onTouchEnd = (event) => {
		const isTap = !moved && !isLogoLongPressed;
		endPressLogo(event);
		if (isTap) {
			node.click();
		}
	};
	node.addEventListener("touchstart", onTouchStart, { passive: false });
	node.addEventListener("touchmove", onTouchMove, { passive: true });
	node.addEventListener("touchend", onTouchEnd);
	node.addEventListener("touchcancel", endPressLogo);
	return { destroy() {
		node.removeEventListener("touchstart", onTouchStart);
		node.removeEventListener("touchmove", onTouchMove);
		node.removeEventListener("touchend", onTouchEnd);
		node.removeEventListener("touchcancel", endPressLogo);
	} };
};
onDestroy(() => {
	if (logoPressTimer) {
		clearTimeout(logoPressTimer);
		logoPressTimer = null;
	}
});
$: {
	lobbyHref = toSiteUrl("/", $loggedIn);
}
</script>

<div class="logo-container">
	<a
		href={lobbyHref}
		on:mousedown={startPressLogo}
		on:mouseup={endPressLogo}
		on:mouseleave={endPressLogo}
		use:logoTouch
		on:click={logoClick}
		class="logo-link"
	>
		<!-- Desktop Logo -->
		<div
			class="logo-desktop"
			class:logo-desktop-alternative={useAlternative || useAlternative4}
			class:logo-desktop-alternative2={useAlternative2}
			class:logo-desktop-unbounded={useUnboundedLogoWidth}
		>
			<img src={desktopLogoPath} draggable="false" alt={$t(Verbiage.site_logo_alt, 'alts')} />
		</div>

		<!-- Mobile Logo -->
		<div
			class="logo-mobile"
			class:logo-mobile-alternative={useAlternative || useAlternative4}
			class:logo-mobile-alternative2={useAlternative2}
			class:logo-mobile-alternative4={useAlternative4}
			class:logo-mobile-desktop={useDesktopLogoOnMobileByBrand}
			class:logo-mobile-game={useDesktopLogoOnMobileGame}
		>
			<img src={mobileLogoPath} draggable="false" alt={$t(Verbiage.site_logo_alt, 'alts')} />
		</div>
	</a>
</div>

<style>
	.logo-container {
		display: flex;
		height: 4rem;
		width: 100%;
		align-items: center;
		justify-content: flex-start;
		padding-top: 0px;
		padding-bottom: 0px;
		padding-left: 0px;
		padding-right: 0px
}

	.logo-link {
		display: flex;
		align-items: center
}

	/* Suppress the native long-press menu / link preview so it can't cancel the dev-settings long-press */
	.logo-link,
	.logo-link img {
		cursor: pointer;
		-webkit-user-select: none;
		   -moz-user-select: none;
		        user-select: none;
		-webkit-touch-callout: none;
		-webkit-user-drag: none
}

	.logo-link img {
		pointer-events: none
}

	/* Desktop logo styling */
	.logo-desktop {
		display: flex;
		height: 40px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}

	.logo-desktop img {
		height: 100%;
		max-height: 40px;
		width: auto;
		max-width: 125px;
		-o-object-fit: contain;
		   object-fit: contain
}

	@media (min-width: 1280px) {
		.logo-desktop-unbounded img {
				max-width: none
		}
	}

	/* Mobile logo styling */
	.logo-mobile {
		display: none;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1))
}

	.logo-mobile img {
		margin-right: 1rem;
		height: auto;
		max-height: 40px;
		width: auto;
		max-width: 40px;
		-o-object-fit: contain;
		   object-fit: contain
}

	.logo-mobile-alternative img {
		height: 55px;
		width: 55px;
		max-height: 55px;
		max-width: 55px
}

	.logo-mobile-alternative2 img {
		height: auto;
		width: 100%;
		max-height: 40px;
		max-width: 95%
}

	.logo-mobile-alternative4 img {
		height: auto;
		width: 80px;
		max-height: unset;
		max-width: 80px
}

	/* Responsive breakpoints */
	@media (min-width: 641px) {
		.logo-desktop {
				display: flex
		}

		.logo-mobile {
				display: none
		}
	}

	@media (max-width: 640px) {
		.logo-desktop {
				display: none
		}

		.logo-mobile {
				display: flex
		}
	}

	@media (min-width: 1024px) {
		.logo-desktop-alternative2 img {
				max-width: 182px
		}
	}

	.logo-mobile-game img {
		margin-right: 1rem;
		height: 100%;
		max-height: 40px;
		width: auto;
		max-width: 125px;
		-o-object-fit: contain;
		   object-fit: contain
}

	.logo-mobile-desktop img {
		margin-right: 1rem;
		height: 100%;
		max-height: 36px;
		width: auto;
		max-width: 95px;
		-o-object-fit: contain;
		   object-fit: contain
}
</style>
